/**
 * SPRINT V4.1 — Contextual Push Engine.
 *
 * Rule-based, deterministic. NO genéricos. Each rule produces an emotional,
 * personalised line tied to: nation rank, rival gap, live prediction,
 * claim window, or rival overtake. Pure logic + PostHog telemetry; does
 * NOT touch FCM/Twilio configuration (handled by /lib/pr33 adapters when
 * present).
 */
import { trackEvent } from "@/lib/audit";
import { getNationFlag, getNationName } from "@/lib/share/nationWarsShare";

export type PushContextKind =
  | "nation_rank_drop"
  | "rival_gap_close"
  | "prediction_alive"
  | "claim_window"
  | "rival_overtake";

export interface PushContextInput {
  nationCode?: string | null;
  previousRank?: number | null;
  currentRank?: number | null;
  rivalNationCode?: string | null;
  rivalGapPoints?: number | null;
  livePredictionMatchId?: string | null;
  claimWindowMinutesLeft?: number | null;
  rivalJustOvertook?: boolean;
}

export interface PushContextMessage {
  kind: PushContextKind;
  title: string;
  body: string;
  priority: number; // higher = more urgent
  deeplink: string;
}

const DEDUPE_KEY = "vs:v4:push:lastSent";
const COOLDOWN_MS = 1000 * 60 * 30; // 30 min between any context push

function lastSent(): number {
  try { return Number(localStorage.getItem(DEDUPE_KEY) ?? 0); } catch { return 0; }
}
function markSent() {
  try { localStorage.setItem(DEDUPE_KEY, String(Date.now())); } catch { /* noop */ }
}

/** Evaluate all rules and pick the highest-priority message. */
export function evaluatePushContext(input: PushContextInput): PushContextMessage | null {
  const out: PushContextMessage[] = [];
  const flag = getNationFlag(input.nationCode ?? null);
  const name = input.nationCode ? getNationName(input.nationCode) : "Tu nación";
  const rivalFlag = getNationFlag(input.rivalNationCode ?? null);
  const rivalName = input.rivalNationCode ? getNationName(input.rivalNationCode) : "el rival";

  if (
    input.previousRank != null &&
    input.currentRank != null &&
    input.currentRank > input.previousRank
  ) {
    out.push({
      kind: "nation_rank_drop",
      title: `${flag} ${name} perdió el puesto #${input.previousRank}`,
      body: `Ahora estás en #${input.currentRank}. Recupera tu posición.`,
      priority: 80,
      deeplink: "/nation-wars",
    });
  }

  if (input.rivalJustOvertook) {
    out.push({
      kind: "rival_overtake",
      title: `🚨 ${rivalFlag} ${rivalName} acaba de superarte`,
      body: `Responde antes de que se aleje. Predicción rápida disponible.`,
      priority: 100,
      deeplink: "/nation-wars",
    });
  }

  if (input.rivalGapPoints != null && input.rivalGapPoints > 0 && input.rivalGapPoints <= 500) {
    out.push({
      kind: "rival_gap_close",
      title: `🔥 ${name} está a ${input.rivalGapPoints} pts de superar a ${rivalName}`,
      body: `Una predicción acertada puede cambiar el ranking.`,
      priority: 70,
      deeplink: "/nation-wars",
    });
  }

  if (input.livePredictionMatchId) {
    out.push({
      kind: "prediction_alive",
      title: `⚽ Tu predicción sigue viva`,
      body: `El partido está en juego. Súmate al hype en vivo.`,
      priority: 60,
      deeplink: `/?match=${encodeURIComponent(input.livePredictionMatchId)}`,
    });
  }

  if (input.claimWindowMinutesLeft != null && input.claimWindowMinutesLeft > 0 && input.claimWindowMinutesLeft <= 10) {
    out.push({
      kind: "claim_window",
      title: `🏆 Quedan ${input.claimWindowMinutesLeft} min para reclamar tus puntos`,
      body: `Tu recompensa expira pronto.`,
      priority: 90,
      deeplink: "/mi-identidad",
    });
  }

  out.sort((a, b) => b.priority - a.priority);
  return out[0] ?? null;
}

/** Dispatch a context push (records analytics + returns the chosen message). */
export function dispatchContextualPush(input: PushContextInput, opts: { force?: boolean } = {}): PushContextMessage | null {
  const msg = evaluatePushContext(input);
  if (!msg) return null;
  if (!opts.force && Date.now() - lastSent() < COOLDOWN_MS) return null;
  markSent();
  void trackEvent("push_context_sent", {
    kind: msg.kind, nation: input.nationCode ?? null, priority: msg.priority,
  });
  return msg;
}

export function recordPushOpened(msg: PushContextMessage): void {
  void trackEvent("push_context_opened", { kind: msg.kind });
}
export function recordPushConverted(msg: PushContextMessage): void {
  void trackEvent("push_context_converted", { kind: msg.kind });
}